import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  Input,
  Modal,
  Segmented,
  Spinner,
  Table,
  type BadgeVariant,
  type TableColumn,
} from "../ds";
import { api, errorText, type BootstrapToken } from "../lib/api";
import { fmtDateTime } from "../lib/format";
import { CopyValue } from "./CopyValue";

// Enrollment-Token: in der Praxis einer je Router (Obergrenze 1), deshalb ist
// das Feld damit vorbelegt. Eine höhere Obergrenze macht daraus einen
// Serientoken für ein ganzes Image — der Mechanismus kann das, gepflegt wird es
// nicht. Der Token weist die Anmeldung als von uns kommend aus; die Schranke ist
// die Freigabe daneben. Verbraucht ist ein Platz erst mit dem ausgestellten
// Zertifikat, nicht schon mit dem Antrag.

function state(t: BootstrapToken): { label: string; variant: BadgeVariant } {
  if (t.revokedAt) return { label: "widerrufen", variant: "danger" };
  if (t.expiresAt && new Date(t.expiresAt) <= new Date())
    return { label: "abgelaufen", variant: "neutral" };
  if (t.maxUses > 0 && t.uses >= t.maxUses) return { label: "voll", variant: "warn" };
  return { label: "aktiv", variant: "success" };
}

/** Kann damit noch ein Router enrollen? Bei einem Token je Router wächst die
 *  Liste mit der Flotte — die zwei offenen Zeilen stünden sonst unter fünfzig
 *  erledigten. */
const isActive = (t: BootstrapToken) => state(t).label === "aktiv";

function columns(busy: string | null, revoke: (id: string) => void): TableColumn<BootstrapToken>[] {
  return [
    { key: "label", header: "Bezeichnung", cell: (t) => t.label },
    {
      key: "uses",
      header: "Verbraucht",
      cell: (t) => (t.maxUses > 0 ? `${t.uses} / ${t.maxUses}` : `${t.uses} / ∞`),
    },
    {
      key: "expires",
      header: "Gültig bis",
      hideOnMobile: true,
      cell: (t) => (t.expiresAt ? fmtDateTime(t.expiresAt) : "unbefristet"),
    },
    {
      key: "state",
      header: "Status",
      cell: (t) => {
        const s = state(t);
        return <Badge variant={s.variant}>{s.label}</Badge>;
      },
    },
    {
      key: "actions",
      header: "",
      align: "right",
      className: "actions",
      cell: (t) =>
        t.revokedAt ? null : (
          <Button
            size="sm"
            variant="danger-outline"
            iconName="close"
            loading={busy === t.id}
            onClick={() => revoke(t.id)}
          >
            Widerrufen
          </Button>
        ),
    },
  ];
}

/** Neuen Token anlegen. Der Klartext geht genau einmal über die Leitung —
 *  deshalb steht er direkt hier und nicht in einer Liste, die man neu lädt. */
function NewToken({ onCreated }: { onCreated: () => void }) {
  const [label, setLabel] = useState("");
  // Vorbelegt mit 1: ein Token je Router ist der Normalfall, alles andere eine
  // bewusste Entscheidung. Sieben Tage decken die Spanne zwischen Anlegen und
  // Eintippen — danach braucht ihn niemand mehr, der Router hat sein Zertifikat.
  const [maxUses, setMaxUses] = useState("1");
  const [days, setDays] = useState("7");
  const [fresh, setFresh] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function create() {
    setBusy(true);
    setError(null);
    try {
      const created = await api.createBootstrapToken({
        label: label.trim(),
        maxUses: Number(maxUses) || 0,
        days: Number(days) || 0,
      });
      setFresh(created.token);
      setLabel("");
      setMaxUses("1");
      setDays("7");
      onCreated();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      {fresh && (
        <Alert variant="success" title="Token — erscheint nur einmal">
          <div className="flex flex-col gap-2">
            <CopyValue value={fresh} className="text-sm" />
            <span>
              Auf dem Router:{" "}
              <code>
                uci set eul-agent.agent.bootstrap_token=&apos;…&apos; &amp;&amp; uci commit
              </code>{" "}
              oder LuCI → „eul-agent“.
            </span>
          </div>
        </Alert>
      )}
      {error && (
        <Alert variant="danger" title="Ging nicht">
          {error}
        </Alert>
      )}

      <div className="flex flex-wrap items-end gap-3">
        <Input
          label="Bezeichnung"
          placeholder="2026-07 WE826"
          value={label}
          fieldClassName="grow min-w-[14rem]"
          onChange={(e) => setLabel(e.target.value)}
        />
        <Input
          label="Obergrenze"
          type="number"
          min={0}
          placeholder="0 = unbegrenzt"
          helperText="1 = ein Router"
          value={maxUses}
          fieldClassName="w-40"
          onChange={(e) => setMaxUses(e.target.value)}
        />
        <Input
          label="Gültig (Tage)"
          type="number"
          min={0}
          placeholder="0 = unbefristet"
          helperText="0 = unbefristet"
          value={days}
          fieldClassName="w-40"
          onChange={(e) => setDays(e.target.value)}
        />
        <Button
          iconName="plus"
          disabled={!label.trim()}
          loading={busy}
          onClick={() => void create()}
        >
          Anlegen
        </Button>
      </div>
    </div>
  );
}

export function BootstrapTokens() {
  const [rows, setRows] = useState<BootstrapToken[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  // Widerrufen ist endgültig — es gibt kein Zurücknehmen (enroll.revoke_token).
  const [confirmRevoke, setConfirmRevoke] = useState<BootstrapToken | null>(null);
  const [filter, setFilter] = useState<"active" | "all">("active");

  const load = useCallback(() => {
    api
      .listBootstrapTokens()
      .then(setRows)
      .catch(() => setRows([]));
  }, []);
  useEffect(load, [load]);

  async function revoke() {
    const target = confirmRevoke;
    setConfirmRevoke(null);
    if (!target) return;
    setBusy(target.id);
    try {
      await api.revokeBootstrapToken(target.id);
      load();
    } finally {
      setBusy(null);
    }
  }

  const shown = rows === null ? null : filter === "all" ? rows : rows.filter(isActive);
  const hidden = rows === null ? 0 : rows.length - (shown?.length ?? 0);

  return (
    <>
      <Card title="Token anlegen">
        <NewToken onCreated={load} />
      </Card>

      <Card
        title="Bootstrap-Token"
        bodyPadding={false}
        actions={
          <Segmented
            options={[
              { value: "active", label: "Aktiv" },
              { value: "all", label: rows ? `Alle (${rows.length})` : "Alle" },
            ]}
            value={filter}
            onChange={setFilter}
          />
        }
      >
        {shown === null ? (
          <div className="flex justify-center p-6">
            <Spinner />
          </div>
        ) : shown.length === 0 ? (
          <EmptyState
            iconName="key"
            title={filter === "active" ? "Kein aktiver Token" : "Keine Token"}
            description={hidden ? `${hidden} erledigte unter „Alle“.` : "Ohne Token kein Antrag."}
          />
        ) : (
          <Table
            columns={columns(busy, (id) =>
              setConfirmRevoke(shown.find((t) => t.id === id) ?? null),
            )}
            rows={shown}
            rowKey={(t) => t.id}
          />
        )}
      </Card>

      <Modal
        open={confirmRevoke !== null}
        onClose={() => setConfirmRevoke(null)}
        title={`Token „${confirmRevoke?.label}“ widerrufen?`}
        size="sm"
        actions={
          <>
            <Button variant="ghost" onClick={() => setConfirmRevoke(null)}>
              Abbrechen
            </Button>
            <Button
              variant="danger"
              iconName="close"
              loading={busy === confirmRevoke?.id}
              onClick={() => void revoke()}
            >
              Widerrufen
            </Button>
          </>
        }
      >
        <p className="text-sm text-content-2">
          Endgültig. Anträge damit werden ab sofort abgewiesen — auch bereits gestellte, die noch
          auf Freigabe warten.
        </p>
      </Modal>
    </>
  );
}
